import React, { useCallback } from "react";
import { IParamFilterShipment, IShipmentInfo } from "../../models/Tracking";
import { useShipmentInfo } from "../../query/Tracking";
import { Button, Input, Table, TableColumnType } from "antd";
import ModalAddShipmentInfo from "../../components/modal/ModalAddShipmentInfo";
import ModalEditShipmentInfo from "../../components/modal/ModalEditShipmentInfo";
import { debounce } from "lodash";

const Shipments: React.FC = () => {
  const [params, setParams] = React.useState<IParamFilterShipment>({
    page: 1,
    limit: 10,
  });
  const [open, setOpen] = React.useState<boolean>(false);
  const [openEdit, setOpenEdit] = React.useState<boolean>(false);
  const [selectedShipment, setSelectedShipment] =
    React.useState<IShipmentInfo | null>(null);
  const { data: shipments, isLoading } = useShipmentInfo(params);
  // eslint-disable-next-line
  const handleSearch = useCallback(
    debounce((field: keyof IParamFilterShipment, value: string) => {
      setParams((prev) => ({
        ...prev,
        [field]: value,
        page: 1,
      }));
    }, 500),
    []
  );

  const renderSearchInput = (
    field: keyof IParamFilterShipment,
    placeholder: string
  ) => (
    <div style={{ padding: 8 }}>
      <Input
        placeholder={placeholder}
        onChange={(e) => handleSearch(field, e.target.value)}
        onPressEnter={() => {
          setParams({
            ...params,
            page: 1,
          });
        }}
      />
    </div>
  );

  const column: TableColumnType<IShipmentInfo>[] = [
    {
      title: "HAWB/HBL",
      dataIndex: "hawbHbl",
      key: "hawbHbl",
      align: "center",
      filterDropdown: () => renderSearchInput("hawbHbl", "Search HAWB/HBL"),
    },
    {
      title: "Dịch vụ",
      dataIndex: "carrierForwarder",
      key: "carrierForwarder",
      align: "center",
      filterDropdown: () =>
        renderSearchInput("carrierForwarder", "Search Dịch vụ"),
    },
    {
      title: "PTVC",
      dataIndex: "shipmentType",
      key: "shipmentType",
      align: "center",
    },
    {
      title: "NCC",
      dataIndex: "seller",
      key: "seller",
      align: "center",
    },
    {
      title: "Thời gian đến dự tính",
      dataIndex: "ata",
      key: "ata",
      align: "center",
    },
    {
      title: "Ngày mở TK",
      dataIndex: "openDate",
      key: "openDate",
      align: "center",
      render: (value: string) => {
        return value ? value : "Chưa cập nhật";
      },
    },
    {
      title: "Số TK",
      dataIndex: "declarationNumber",
      key: "declarationNumber",
      align: "center",
      render: (value: string) => {
        return value ? value : "Chưa cập nhật";
      },
    },
    {
      title: "Số nhánh",
      dataIndex: "branchNumber",
      key: "branchNumber",
      align: "center",
      render: (value: string) => {
        return value ? value : "Chưa cập nhật";
      },
    },
    {
      title: "Phân luồng",
      dataIndex: "classification",
      key: "classification",
      align: "center",
      render: (value: string) => {
        switch (value) {
          case "GREEN":
            return "XANH";
          case "YELLOW":
            return "VÀNG";
          case "RED":
            return "ĐỎ";
          default:
            return "XANH";
        }
      },
    },
    {
      title: "Ngày kết thúc xử lý TK",
      dataIndex: "declarationEndDate",
      key: "declarationEndDate",
      align: "center",
      render: (value: string) => {
        return value ? value : "Chưa cập nhật";
      },
    },
    {
      title: "Trạng thái TK",
      dataIndex: "declarationStatus",
      key: "declarationStatus",
      align: "center",
      render: (value: boolean) => {
        return value ? "Đã xong" : "Chưa xong";
      },
    },
    {
      title: "Số ngày xử lí TK",
      dataIndex: "declarationProcessingDays",
      key: "declarationProcessingDays",
      align: "center",
      render: (value: string) => {
        return value ? value : "Chưa cập nhật";
      },
    },
    {
      title: "Số ngày xử lí lô hàng",
      dataIndex: "shipmentProcessingDays",
      key: "shipmentProcessingDays",
      align: "center",
      render: (value: string) => {
        return value ? value : "Chưa cập nhật";
      },
    },
    {
      title: "Số ngày chậm tiến độ",
      dataIndex: "daysDelayedScheduled",
      key: "daysDelayedScheduled",
      align: "center",
      render: (value: string) => {
        return value ? value : "Chưa cập nhật";
      },
    },
    {
      title: "Ngày báo phát",
      dataIndex: "releaseDate",
      key: "releaseDate",
      align: "center",
      render: (value: string) => {
        return value ? value : "Chưa cập nhật";
      },
    },
    {
      title: "Ngày về kho",
      dataIndex: "warehouseArrivalDate",
      key: "warehouseArrivalDate",
      align: "center",
      render: (value: string) => {
        return value ? value : "Chưa cập nhật";
      },
    },
    {
      title: "Trạng thái hàng về",
      dataIndex: "deliveryStatus",
      key: "deliveryStatus",
      align: "center",
      render: (value: boolean) => {
        return value ? "Đã về kho" : "Chưa về kho";
      },
    },
    {
      title: "Ghi chú",
      dataIndex: "notes",
      key: "notes",
      align: "center",
      render: (value: string) => {
        return value ? value : "Chưa cập nhật";
      },
    },
    {
      title: "CDS PIC",
      dataIndex: "cdsPic",
      key: "cdsPic",
      align: "center",
    },
  ];
  return (
    <div className="p-4">
      <div className="flex justify-end mb-4">
        <Button type="primary" onClick={() => setOpen(true)}>
          Thêm vận đơn
        </Button>
      </div>
      <Table
        size="small"
        columns={column}
        dataSource={shipments?.data}
        onRow={(record: IShipmentInfo) => {
          return {
            onClick: () => {
              setSelectedShipment(record);
              setOpenEdit(true);
            },
          };
        }}
        loading={isLoading}
        scroll={{ x: 1000 }}
        pagination={
          shipments?.pagination && {
            ...shipments?.pagination,
            onChange: (page, pageSize) => {
              setParams({ ...params, page, limit: pageSize });
            },
          }
        }
      />
      <ModalAddShipmentInfo
        isOpen={open}
        onClose={() => setOpen(false)}
        params={params}
      />
      {selectedShipment ? (
        <ModalEditShipmentInfo
          isOpen={openEdit}
          onClose={() => setOpenEdit(false)}
          params={params}
          shipment={selectedShipment}
        />
      ) : null}
    </div>
  );
};

export default Shipments;
